/* eslint-disable camelcase */
// TODO: Uncomment plot edit sections when FE plot edit functionality is working
import { useMutationState } from '@tanstack/react-query';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownItemLink,
  //  DropdownItemLinkProps,
  DropdownItemProps,
  DropdownTrigger,
  Loader,
  Pagination,
  RiArrowRightSLine,
  RiDeleteBinLine,
  //  RiEditLine,
  RiEyeLine,
  RiMore2Line,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { Plot, PlotStatusEnum, PlotWithFacts } from '@/api/rest/resources/types/plot';
import { PlotLabelValue } from '@/api/rest/resources/types/plotGroup';
import { WidgetSlotConfiguration } from '@/api/rest/resources/types/widget';
import { withSuspenseBoundary } from '@/components';
import { useSetDetailsPlotGroup } from '@/components/PlotGroups/hooks/useSetPlotGroup';
import { useActivePlotGroup, useActivePlotGroupPlotLabel } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { usePlotGroupSortConfiguration } from '@/components/widgets/config';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { UNANALYZABLE_PLOT_STATUS_TYPES } from '@/config/constants';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
// import { Logger } from '@/lib/logs/logger';
import { PlotRow } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/plot-list/Cells';
import {
  // CheckboxCell,
  LabelNameCell,
  NameCell,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plots/components/Cells';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { useOrganizationId } from '../../hooks/useOrganizationId';
import { usePlotLabelNameValues } from '../../hooks/usePlotLabels';
import { usePlotsForOrganization } from '../../hooks/usePlotsForOrganization';
import {
  PAGE_SIZE,
  ProjectTypeAccessorKey,
} from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { DeletePlotWithConfirmation } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/DeletePlotWithConfirmation';
import { ToggleSortButton } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/ToggleSortButton';
import { useDestinationPath } from '../hooks/useDestinationPath';
import { MetadataColumn, useGetCustomCells } from '../hooks/useGetCustomCells';

export type PlotListDesktopProps = {
  plotFilters?: Record<string, unknown>;
  customColumns?: MetadataColumn[];
  showSort?: boolean;
  showActions?: boolean;
  pageSize?: number;
  showPagination?: boolean;
  organizationIdArg?: string;
  defaultRowSelected?: boolean;
  enableRowSelection?: boolean;
};

export const PlotListDesktop: FC<PlotListDesktopProps> = withSuspenseBoundary(
  (props) => {
    return <PlotListDesktopBase {...props} />;
  },
  <div className='bg-white-100 rounded-2xl border border-neutral-black-12'>
    <Loader className='min-h-[300px] relative' />
  </div>,
);

const PlotListDesktopBase: FC<PlotListDesktopProps> = ({
  plotFilters,
  customColumns,
  showSort = true,
  showActions = true,
  pageSize = PAGE_SIZE,
  showPagination = true,
  organizationIdArg,
  defaultRowSelected = false,
  enableRowSelection = false,
}) => {
  const { t } = useTranslation();
  const membershipType = useMembershipType();
  const sortWidgetConfiguration = usePlotGroupSortConfiguration();
  const { pathname } = useLocation();
  const pathSet = usePathSet();

  const defaultColumns = useGetCustomCells();
  const customCells = customColumns ?? defaultColumns;

  const [plotForDeletion, setPlotForDeletion] = useState<Plot | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const parentPlotGroupIdArg = searchParams.get('plotGroupId') || undefined;

  const setCurrentPage = (page: number) => {
    setSearchParams(
      {
        page: `${page}`,
        ...(parentPlotGroupIdArg && { plotGroupId: parentPlotGroupIdArg }),
      },
      { preventScrollReset: true },
    );
  };
  const organisationId = useOrganizationId();

  const activePlotGroup = useActivePlotGroup();
  const paginatedPlotsForProjectQuery = usePlotsForOrganization(
    organizationIdArg ?? organisationId,
    plotFilters ?? {},
    pageSize,
    (currentPage - 1) * pageSize,
    activePlotGroup.id ?? undefined,
    parentPlotGroupIdArg,
  );

  const { count: totalPlotCount, results: plotDetails, asset_column_name } = paginatedPlotsForProjectQuery.data;

  const plotLabel = useActivePlotGroupPlotLabel();
  const plotLabelValues = usePlotLabelNameValues(plotLabel?.id || null).data?.results ?? null;

  const isRenderingPlotLabels = !!plotLabel?.id;
  const isPlotsPage = !!matchPath(pathSet.plots, pathname);

  const factsReport = (facts: PlotReportFactElement[]) => {
    return facts.reduce<Record<string, PlotReportFactElement>>((accumulator, item) => {
      if (!accumulator[item.name]) {
        accumulator[item.name] = item;
      }
      return accumulator;
    }, {});
  };

  const plots = useMemo(() => {
    return plotDetails.map((plotDetail: PlotWithFacts) => {
      return { ...plotDetail, facts: factsReport(plotDetail.facts) };
    });
  }, [plotDetails]);

  const confirmPlotDelete = (plot: Plot) => {
    setPlotForDeletion(plot);
  };

  const columns = useMemo<ColumnDef<PlotRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <ToggleSortButton column={column} className='-ml-2.5'>
            {asset_column_name ?? t('shared.plots.tableHeader.name')}
          </ToggleSortButton>
        ),
        cell: (context) =>
          isRenderingPlotLabels || !context.row?.original?.project_id ? (
            <LabelNameCell {...context} />
          ) : (
            <NameCell {...context} />
          ),
      },
      ...customCells,
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (isRenderingPlotLabels || !row?.original?.project_id || !showActions) {
            return null;
          }

          return <TableActionCell row={row} confirmPlotDelete={confirmPlotDelete} currentPage={currentPage} />;
        },
      },
    ],
    [customCells, t, currentPage, isRenderingPlotLabels, asset_column_name, showActions],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    Object.fromEntries(Array.from(Array(plots.length).keys()).map((index) => [index.toString(), defaultRowSelected])),
  );

  const table = useReactTable({
    data: plots as unknown as PlotRow[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
    state: {
      sorting,
      rowSelection,
    },
  });

  const patchPlotMutationStatus = useMutationState({
    filters: { mutationKey: [membershipType, 'plot'] },
    select: (mutation) => mutation.state.status,
  }).at(-1);

  const loading = patchPlotMutationStatus === 'pending' || paginatedPlotsForProjectQuery.isRefetching;

  const renderPlotLabel = isPlotsPage && isRenderingPlotLabels;

  const rowsCountText = renderPlotLabel
    ? `${totalPlotCount} ${plotLabel.name}`
    : t('shared.plots.pagination.titleMobile', {
        count: totalPlotCount,
      });

  const widgetConfiguration = { ...(showSort && sortWidgetConfiguration) } as WidgetSlotConfiguration;
  return (
    <>
      <Stack spacing={8} data-testid='plot-list-desktop' className='w-full overflow-hidden scrollbar-hidden'>
        <Stack className='flex flex-row items-center justify-between gap-2'>
          {showSort && <span className='typography-overline'>{rowsCountText}</span>}
          <Stack direction='row' spacing={2}>
            <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
          </Stack>
        </Stack>
        <div className='relative bg-white-100 rounded-2xl border border-neutral-black-12'>
          {loading && (
            <Stack center className='absolute left-0 top-0 z-10 h-full w-full bg-neutral-white-50'>
              <Spinner size={10} />
            </Stack>
          )}
          <Table data-testid='plots-table'>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const renderPlotLabelView = renderPlotLabel || isNullish(row.original?.project_id);

                return renderPlotLabelView ? (
                  <TablePlotLabelRow
                    key={row.id}
                    row={row.original as PlotRow}
                    cells={row.getVisibleCells() as unknown as Cell<PlotRow, unknown>[]}
                    plotLabelValues={plotLabelValues}
                    customCells={customCells}
                    enableRowSelection={enableRowSelection}
                  />
                ) : (
                  <TablePlotRow
                    key={row.id}
                    plot={row.original as PlotRow}
                    cells={row.getVisibleCells() as unknown as Cell<PlotRow, unknown>[]}
                    customCells={customCells}
                    enableRowSelection={enableRowSelection}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
        {showPagination && (
          <Pagination
            className='typography-overline text-text-secondary mx-auto'
            currentPage={currentPage}
            totalCount={totalPlotCount}
            pageSize={pageSize}
            onPageChange={(nextPage) => {
              setCurrentPage(nextPage);
            }}
            data-testid='pagination'
          />
        )}
      </Stack>

      <DeletePlotWithConfirmation plot={plotForDeletion} onClose={() => setPlotForDeletion(null)} />
    </>
  );
};

const TablePlotLabelRow = ({
  row,
  cells,
  plotLabelValues,
  customCells,
  enableRowSelection,
}: {
  row: PlotRow;
  cells: Cell<PlotRow, unknown>[];
  plotLabelValues: PlotLabelValue[] | null;
  customCells: MetadataColumn[];
  enableRowSelection?: boolean;
}) => {
  const { setSecondaryPlotGroupPlotLabel } = useSetDetailsPlotGroup();
  const pathSet = usePathSet();
  const navigate = useNavigate();

  const labelValue = plotLabelValues?.find((label) => label.value === row.id);

  const onRowClick = async () => {
    if (!labelValue) {
      return;
    }

    const { id } = await setSecondaryPlotGroupPlotLabel([labelValue]);

    const destinationPath = buildPath(pathSet.details, {
      queryParams: { plotGroupId: id },
    });

    navigate(destinationPath, {
      state: {
        previousPath: pathSet.plots,
        plotPage: '1',
      },
    });
  };

  return (
    <TableRow data-testid='plot-row' className='group/plot-row' onClick={!enableRowSelection ? onRowClick : undefined}>
      {cells.map((cell: Cell<PlotRow, unknown>) => {
        /** Do not render succeeding columns so that biodiversity CTA can span over those columns
         *  If more columns need to be spanned over, the accessoryKeys of those columns need to be added into this condition
         */
        if (
          [ProjectTypeAccessorKey.carbonStorageBg, ProjectTypeAccessorKey.waterHoldingCapacity].includes(
            cell.column.id as ProjectTypeAccessorKey,
          ) &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(row.status)
        ) {
          return null;
        }

        if (
          cell.column.id === ProjectTypeAccessorKey.biodiversityZone &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(row.status)
        ) {
          return (
            <TableCell key={cell.id} colSpan={customCells.length}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        }

        return <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>;
      })}
    </TableRow>
  );
};

const TablePlotRow = ({
  plot,
  cells,
  customCells,
  enableRowSelection,
}: {
  plot: PlotRow;
  cells: Cell<PlotRow, unknown>[];
  customCells: MetadataColumn[];
  enableRowSelection?: boolean;
}) => {
  const { getDestinationPath } = useDestinationPath();
  const destinationPath = getDestinationPath(plot);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const onRowClick = () => {
    return (
      destinationPath &&
      navigate(destinationPath, {
        state: {
          previousPath: buildPath(pathname, {
            queryParams: {
              page: currentPage,
            },
          }),
          plotPage: `${currentPage}`,
        },
      })
    );
  };

  return (
    <TableRow
      data-testid='plot-row'
      onClick={!enableRowSelection ? onRowClick : undefined}
      className='group/plot-row hover:cursor-pointer'
    >
      {cells.map((cell: Cell<PlotRow, unknown>) => {
        /** Do not render succeeding columns so that biodiversity CTA can span over those columns
         *  If more columns need to be spanned over, the accessoryKeys of those columns need to be added into this condition
         */
        if (
          [ProjectTypeAccessorKey.carbonStorageBg, ProjectTypeAccessorKey.waterHoldingCapacity].includes(
            cell.column.id as ProjectTypeAccessorKey,
          ) &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)
        ) {
          return null;
        }

        if (
          cell.column.id === ProjectTypeAccessorKey.biodiversityZone &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)
        ) {
          return (
            <TableCell key={cell.id} colSpan={customCells.length}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        }

        return <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>;
      })}
    </TableRow>
  );
};

const TableActionCell = ({
  row,
  confirmPlotDelete,
  currentPage,
}: {
  row: Row<PlotRow>;
  confirmPlotDelete: (plot: Plot) => void;
  currentPage: number;
}) => {
  const pathSet = usePathSet();
  const plot = row.original;
  const projectId = plot.project_id as string;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const membershipType = useMembershipType();
  const isValid = plot.status !== PlotStatusEnum.invalid;

  // TODO: Uncomment when FE plot edit functionality is working
  /* const editLabel = useMemo(() => {
  *   const exhaustivenessCheck = (status: never) => {
  *     Logger.error(`No edit label set for plot status ${status}`);
  *     return t('shared.plots.overflowMenu.editData');
  *   };
  
  *   switch (plot.status) {
  *     case PlotStatusEnum.invalid:
  *       return t('shared.plots.overflowMenu.reviewData');
  *     case PlotStatusEnum.new_plot:
  *       return t('shared.plots.overflowMenu.addData');
  *     case PlotStatusEnum.draft:
  *       return t('shared.plots.overflowMenu.confirmData');
  *     case PlotStatusEnum.ready_to_analyse:
  *     case PlotStatusEnum.scheduled_for_analysis:
  *     case PlotStatusEnum.analysed:
  *     case PlotStatusEnum.failed:
  *       return t('shared.plots.overflowMenu.editData');
  *     default:
  *       return exhaustivenessCheck(plot.status);
  *   }
  * }, [plot.status, t]); */

  if (membershipType === MembershipWithOrganizationTypeEnum.land_steward) {
    return (
      <Dropdown modal={true}>
        <DropdownTrigger data-testid='plot-row-overflow-menu'>
          <div className='shrink-0 rounded-full border border-primary-100 p-2 text-primary-100 hover:bg-neutral-black-4'>
            <RiMore2Line size={24} />
          </div>
        </DropdownTrigger>

        {/* TODO: MVP-CW make dropdown options independent on the project id */}
        <DropdownContent side='bottom' sideOffset={10}>
          {isValid && (
            <DropdownItemLink
              disabled={!projectId}
              to={buildPath(pathSet.plot, {
                pathParams: {
                  projectId,
                  plotId: plot.id,
                },
              })}
              state={{
                previousPath: buildPath(pathname, {
                  queryParams: {
                    page: currentPage,
                  },
                }),
                plotPage: `${currentPage}`,
              }}
              leftAdornment={<RiEyeLine />}
            >
              {t('shared.plots.overflowMenu.viewPlot')}
            </DropdownItemLink>
          )}
          {/* TODO: Uncomment when FE plot edit functionality is working */}
          {/* <EditPlotDropdownItemLink
            disabled={false}
            disabledHint={t('shared.projects.plot.tooltips.disabledEditOrDeletePlot')}
            to={buildPath(pathSet.editPlot, {
              pathParams: {
                plotId: plot.id,
                projectId,
              },
            })}
            state={{
              previousPath: buildPath(pathname, {
                queryParams: {
                  page: currentPage,
                },
              }),
              plotPage: `${currentPage}`,
            }}
            leftAdornment={<RiEditLine />}
          >
            {editLabel}
          </EditPlotDropdownItemLink> */}
          <DeletePlotDropdownItem
            disabled={false}
            disabledHint={t('shared.projects.plot.tooltips.disabledEditOrDeletePlot')}
            onClick={() => confirmPlotDelete(plot)}
            leftAdornment={<RiDeleteBinLine />}
          >
            {t('shared.plots.overflowMenu.deletePlot')}
          </DeletePlotDropdownItem>
        </DropdownContent>
      </Dropdown>
    );
  }

  return isValid ? (
    <Link
      to={buildPath(pathSet.plot, {
        pathParams: { projectId, plotId: row.original.id },
      })}
      state={{
        previousPath: buildPath(pathname, {
          queryParams: {
            page: currentPage,
          },
        }),
        plotPage: `${currentPage}`,
      }}
    >
      <Stack center className='h-10 w-10 rounded-full border border-primary-100 hover:bg-neutral-black-4'>
        <RiArrowRightSLine size={24} />
      </Stack>
    </Link>
  ) : null;
};

// TODO: Uncomment when FE plot edit functionality is working
/* type EditPlotDropdownItemLinkProps = DropdownItemLinkProps & {
 *   disabledHint: string;
 * };
 *  */

// TODO: Uncomment when FE plot edit functionality is working
/* const EditPlotDropdownItemLink: FC<EditPlotDropdownItemLinkProps> = ({ children, disabledHint, to, ...delegated }) => {
 *   if (!delegated.disabled) {
 *     return (
 *       <DropdownItemLink to={to} {...delegated}>
 *         {children}
 *       </DropdownItemLink>
 *     );
 *   }
 *
 *   return (
 *     <Tooltip>
 *       <TooltipTrigger asChild>
 *         <DropdownItem {...delegated}>{children}</DropdownItem>
 *       </TooltipTrigger>
 *       <TooltipContent sideOffset={5} side='left'>
 *         {disabledHint}
 *         <TooltipArrow className='fill-white-100' />
 *       </TooltipContent>
 *     </Tooltip>
 *   );
 * };
 *  */
type DeletePlotDropdownItemProps = DropdownItemProps & {
  disabledHint: string;
};

const DeletePlotDropdownItem: FC<DeletePlotDropdownItemProps> = ({ children, disabledHint, ...delegated }) => {
  if (!delegated.disabled) {
    return <DropdownItem {...delegated}>{children}</DropdownItem>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownItem {...delegated}>{children}</DropdownItem>
      </TooltipTrigger>
      <TooltipContent sideOffset={5} side='left'>
        {disabledHint}
        <TooltipArrow className='fill-white-100' />
      </TooltipContent>
    </Tooltip>
  );
};
